import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../utils/api';
import ModalPortal from '../components/ModalPortal';

export default function VersionControlDashboard() {
    const [activeTab, setActiveTab] = useState('working-version'); // 'working-version' | 'merge-queue' | 'releases'
    
    // Active version context
    const [activeVersionId, setActiveVersionId] = useState(localStorage.getItem('activeVersionId') || '00000000-0000-0000-0000-000000000000');
    const [activeVersionName, setActiveVersionName] = useState(localStorage.getItem('activeVersionName') || 'Production');
    
    // Data lists
    const [versions, setVersions] = useState([]);
    const [commits, setCommits] = useState([]);
    const [versionDiff, setVersionDiff] = useState([]);
    const [validationResults, setValidationResults] = useState(null);
    
    // UI states
    const [loadingDiff, setLoadingDiff] = useState(false);
    const [loadingValidation, setLoadingValidation] = useState(false);
    const [loadingData, setLoadingData] = useState(true);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isCommitModalOpen, setIsCommitModalOpen] = useState(false);
    
    // Form inputs
    const [newVersionName, setNewVersionName] = useState('');
    const [newVersionParent, setNewVersionParent] = useState('00000000-0000-0000-0000-000000000000');
    const [commitComment, setCommitComment] = useState('');
    const [releaseName, setReleaseName] = useState('');
    const [releaseSourceVersionId, setReleaseSourceVersionId] = useState('00000000-0000-0000-0000-000000000000');
    
    // Feedback messages
    const [error, setError] = useState(null);
    const [successMsg, setSuccessMsg] = useState(null);

    const navigate = useNavigate();
    const [selectedDiffs, setSelectedDiffs] = useState([]);
    const [modalTitle, setModalTitle] = useState('');
    const [modalCommitComment, setModalCommitComment] = useState('');
    const [modalOnMerge, setModalOnMerge] = useState(null);
    const [modalOnGoToEditor, setModalOnGoToEditor] = useState(null);
    const [isDiffModalOpen, setIsDiffModalOpen] = useState(false);
    const [isMergingState, setIsMergingState] = useState(false);

    const handlePendingChangeClick = (diff) => {
        setSelectedDiffs([diff]);
        setModalTitle(`Change Details: ${diff.name}`);
        setModalCommitComment('');
        setModalOnMerge(null);
        setModalOnGoToEditor(() => () => {
            setIsDiffModalOpen(false);
            if (diff.type === 'Feature') {
                navigate(`/features?featureId=${diff.id || diff.parentId}`);
            } else if (diff.type === 'RuleSet') {
                navigate(`/?rulesetId=${diff.id || diff.parentId}`);
            } else if (diff.type === 'ProductFamily') {
                navigate(`/products?familyId=${diff.id || diff.parentId}`);
            }
        });
        setIsDiffModalOpen(true);
    };

    const handleReviewCommitClick = (commit) => {
        let parsed = [];
        try {
            parsed = JSON.parse(commit.changesJson);
        } catch (e) {}

        setSelectedDiffs(parsed);
        setModalTitle(`Review Commit Details`);
        setModalCommitComment(commit.comment);
        setModalOnGoToEditor(null);
        setModalOnMerge(() => async () => {
            setIsMergingState(true);
            try {
                await handleMergeCommit(commit.id);
                setIsDiffModalOpen(false);
            } catch (err) {
                console.error(err);
            } finally {
                setIsMergingState(false);
            }
        });
        setIsDiffModalOpen(true);
    };

    const isProduction = activeVersionId === '00000000-0000-0000-0000-000000000000';
    const activeVersion = versions.find(b => b.id === activeVersionId);
    const activeVersionStatus = activeVersion ? activeVersion.status : null;
    const isVersionActive = activeVersionStatus === 0 || activeVersionStatus === 'Active' || isProduction;

    useEffect(() => {
        const handleVersionContextChange = () => {
            setActiveVersionId(localStorage.getItem('activeVersionId') || '00000000-0000-0000-0000-000000000000');
            setActiveVersionName(localStorage.getItem('activeVersionName') || 'Production');
            setValidationResults(null);
            setVersionDiff([]);
        };
        window.addEventListener('activeVersionChanged', handleVersionContextChange);
        return () => window.removeEventListener('activeVersionChanged', handleVersionContextChange);
    }, []);

    const loadData = async () => {
        setLoadingData(true);
        setError(null);
        try {
            // Fetch versions
            const versionRes = await apiFetch('/api/versions');
            if (versionRes.ok) {
                const data = await versionRes.json();
                setVersions(data);
            }
            
            // Fetch commits
            const commitRes = await apiFetch('/api/commits');
            if (commitRes.ok) {
                const data = await commitRes.json();
                setCommits(data);
            }
        } catch (err) {
            console.error('Failed to load version control data', err);
            setError('Could not connect to version control services.');
        } finally {
            setLoadingData(false);
        }
    };

    useEffect(() => {
        loadData();
    }, [activeVersionId]);

    // Load active version diff
    useEffect(() => {
        if (isProduction) {
            setVersionDiff([]);
            return;
        }
        
        const fetchDiff = async () => {
            setLoadingDiff(true);
            try {
                const res = await apiFetch(`/api/versions/${activeVersionId}/diff`);
                if (res.ok) {
                    const data = await res.json();
                    setVersionDiff(data);
                } else {
                    setVersionDiff([]);
                }
            } catch (err) {
                console.error('Failed to fetch version diff', err);
            } finally {
                setLoadingDiff(false);
            }
        };

        fetchDiff();
    }, [activeVersionId]);

    const handleCreateVersion = async (e) => {
        e.preventDefault();
        if (!newVersionName.trim()) return;

        setError(null);
        setSuccessMsg(null);
        try {
            const res = await apiFetch('/api/versions', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: newVersionName.trim(),
                    parentVersionId: newVersionParent
                })
            });

            if (res.ok) {
                const newVersion = await res.json();
                setSuccessMsg(`Version "${newVersion.name}" created successfully.`);
                setNewVersionName('');
                setIsCreateModalOpen(false);
                
                // Automatically switch to the newly created version
                localStorage.setItem('activeVersionId', newVersion.id);
                localStorage.setItem('activeVersionName', newVersion.name);
                window.dispatchEvent(new Event('activeVersionChanged'));
                
                await loadData();
            } else {
                const errData = await res.json();
                setError(errData.message || 'Failed to create version.');
            }
        } catch (err) {
            console.error(err);
            setError('Could not connect to version service.');
        }
    };

    const handleRunCompatibilityCheck = async () => {
        setLoadingValidation(true);
        setError(null);
        setValidationResults(null);
        try {
            const res = await apiFetch(`/api/variant/validate-version/${activeVersionId}`, {
                method: 'POST'
            });
            if (res.ok) {
                const data = await res.json();
                setValidationResults(data);
            } else {
                setError('Failed to run compatibility check.');
            }
        } catch (err) {
            console.error(err);
            setError('Could not communicate with Variant Manager service.');
        } finally {
            setLoadingValidation(false);
        }
    };

    const handleSubmitCommit = async (e) => {
        e.preventDefault();
        if (!commitComment.trim()) return;

        setError(null);
        setSuccessMsg(null);
        try {
            const res = await apiFetch(`/api/versions/${activeVersionId}/commit`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    comment: commitComment.trim()
                })
            });

            if (res.ok) {
                setSuccessMsg('Version changes committed successfully. Awaiting administrator merge.');
                setCommitComment('');
                setIsCommitModalOpen(false);
                
                // Reset to Production version since this draft is now frozen/committed
                localStorage.setItem('activeVersionId', '00000000-0000-0000-0000-000000000000');
                localStorage.setItem('activeVersionName', 'Production');
                window.dispatchEvent(new Event('activeVersionChanged'));
                
                await loadData();
            } else {
                const errData = await res.json();
                setError(errData.message || 'Failed to submit commit.');
            }
        } catch (err) {
            console.error(err);
            setError('Could not connect to commit service.');
        }
    };

    const handleMergeCommit = async (commitId) => {
        if (!await window.confirm('Are you sure you want to approve and merge this commit? This will apply draft changes to Production.')) return;

        setError(null);
        setSuccessMsg(null);
        try {
            const res = await apiFetch(`/api/commits/${commitId}/merge`, {
                method: 'POST'
            });

            if (res.ok) {
                setSuccessMsg('Commit merged successfully into Production.');
                
                // If the merged commit is for our active version, switch back to Production
                const mergedCommit = commits.find(c => c.id === commitId);
                if (mergedCommit && mergedCommit.versionId === activeVersionId) {
                    localStorage.setItem('activeVersionId', '00000000-0000-0000-0000-000000000000');
                    localStorage.setItem('activeVersionName', 'Production');
                    window.dispatchEvent(new Event('activeVersionChanged'));
                }

                await loadData();
            } else {
                const errData = await res.json();
                setError(errData.message || 'Failed to merge commit.');
            }
        } catch (err) {
            console.error(err);
            setError('Could not connect to merge service.');
        }
    };

    const handlePublishRelease = async (e) => {
        e.preventDefault();
        if (!releaseName.trim()) return;

        setError(null);
        setSuccessMsg(null);
        try {
            const res = await apiFetch('/api/releases/publish', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: releaseName.trim(),
                    sourceVersionId: releaseSourceVersionId === '00000000-0000-0000-0000-000000000000' ? null : releaseSourceVersionId
                })
            });

            if (res.ok) {
                setSuccessMsg(`Release "${releaseName}" published successfully.`);
                setReleaseName('');
                setReleaseSourceVersionId('00000000-0000-0000-0000-000000000000');
                await loadData();
            } else {
                const errData = await res.json();
                setError(errData.message || 'Failed to publish release.');
            }
        } catch (err) {
            console.error(err);
            setError('Could not connect to release service.');
        }
    };

    const handleRevertCommit = async (commitId) => {
        const commit = commits.find(c => c.id === commitId);
        if (!commit) return;
        if (!await window.confirm(`Are you sure you want to revert Production to the state of this commit?\n\n"${commit.comment}"\n\nThis will overwrite the current Production configuration and create a new revert commit.`)) return;

        setError(null);
        setSuccessMsg(null);
        try {
            const res = await apiFetch(`/api/commits/${commitId}/revert`, {
                method: 'POST'
            });

            if (res.ok) {
                setSuccessMsg('Production successfully reverted to the selected version.');
                window.dispatchEvent(new Event('activeVersionChanged'));
                await loadData();
            } else {
                const errData = await res.json();
                setError(errData.message || 'Failed to revert to the selected commit.');
            }
        } catch (err) {
            console.error(err);
            setError('Could not connect to revert service.');
        }
    };

    if (loadingData) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                <div className="loader"></div>
            </div>
        );
    }

    return (
        <div style={{ padding: '1rem 1.25rem', width: '100%', maxWidth: '100%', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '1rem' }} className="fade-in">
            <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                    <h1 style={{ fontSize: '1.35rem', margin: 0, color: 'var(--text-primary)' }}>Version Control & Release Hub</h1>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', margin: '0.25rem 0 0 0' }}>
                        Manage rule configurations via isolated draft versions, compatibility verification, and official releases.
                    </p>
                </div>
                
                <button
                    onClick={() => setIsCreateModalOpen(true)}
                    className="btn-primary"
                    style={{ background: 'var(--text-primary)', color: 'var(--bg-primary)', display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.6rem 1.25rem' }}
                >
                    <i className="fa-solid fa-code-version"></i> Create Working Version
                </button>
            </header>

            {error && (
                <div style={{ padding: '0.75rem 1rem', background: 'rgba(220,38,38,0.08)', border: '1px solid var(--error)', borderRadius: 'var(--radius)', color: 'var(--error)', fontSize: '0.85rem' }}>
                    <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: '0.5rem' }}></i> {error}
                </div>
            )}
            {successMsg && (
                <div style={{ padding: '0.75rem 1rem', background: 'rgba(5,150,105,0.08)', border: '1px solid var(--success)', borderRadius: 'var(--radius)', color: 'var(--success)', fontSize: '0.85rem' }}>
                    <i className="fa-solid fa-circle-check" style={{ marginRight: '0.5rem' }}></i> {successMsg}
                </div>
            )}

            {/* Main Tabs */}
            <div style={{ display: 'flex', gap: '1rem', borderBottom: '1px solid var(--border)', paddingBottom: '0.5rem' }}>
                <button
                    onClick={() => setActiveTab('working-version')}
                    style={{
                        background: 'transparent',
                        border: 'none',
                        borderBottom: activeTab === 'working-version' ? '2px solid var(--text-primary)' : '2px solid transparent',
                        color: activeTab === 'working-version' ? 'var(--text-primary)' : 'var(--text-secondary)',
                        fontWeight: activeTab === 'working-version' ? 600 : 400,
                        padding: '0.5rem 1rem',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.4rem'
                    }}
                >
                    <i className="fa-solid fa-laptop-code"></i> Active Version Dashboard
                </button>
                <button
                    onClick={() => setActiveTab('merge-queue')}
                    style={{
                        background: 'transparent',
                        border: 'none',
                        borderBottom: activeTab === 'merge-queue' ? '2px solid var(--text-primary)' : '2px solid transparent',
                        color: activeTab === 'merge-queue' ? 'var(--text-primary)' : 'var(--text-secondary)',
                        fontWeight: activeTab === 'merge-queue' ? 600 : 400,
                        padding: '0.5rem 1rem',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.4rem'
                    }}
                >
                    <i className="fa-solid fa-code-pull-request"></i> Merge Queue (Admin)
                </button>
                <button
                    onClick={() => setActiveTab('releases')}
                    style={{
                        background: 'transparent',
                        border: 'none',
                        borderBottom: activeTab === 'releases' ? '2px solid var(--text-primary)' : '2px solid transparent',
                        color: activeTab === 'releases' ? 'var(--text-primary)' : 'var(--text-secondary)',
                        fontWeight: activeTab === 'releases' ? 600 : 400,
                        padding: '0.5rem 1rem',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.4rem'
                    }}
                >
                    <i className="fa-solid fa-tags"></i> Release Management
                </button>
                <button
                    onClick={() => setActiveTab('commit-history')}
                    style={{
                        background: 'transparent',
                        border: 'none',
                        borderBottom: activeTab === 'commit-history' ? '2px solid var(--text-primary)' : '2px solid transparent',
                        color: activeTab === 'commit-history' ? 'var(--text-primary)' : 'var(--text-secondary)',
                        fontWeight: activeTab === 'commit-history' ? 600 : 400,
                        padding: '0.5rem 1rem',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.4rem'
                    }}
                >
                    <i className="fa-solid fa-clock-rotate-left"></i> Commit History
                </button>
            </div>

            {/* Tab Contents */}
            <div style={{ minHeight: '400px' }}>
                {activeTab === 'working-version' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }} className="fade-in">
                        {/* Context Summary card */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--bg-secondary)', padding: '1rem', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
                            <div>
                                <h3 style={{ margin: 0, fontSize: '1.1rem', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    Active Version: <span style={{ color: 'var(--accent)' }}>{activeVersionName}</span>
                                    {activeVersion && !isVersionActive && (
                                        <span style={{
                                            fontSize: '0.7rem',
                                            fontWeight: 700,
                                            padding: '0.15rem 0.4rem',
                                            borderRadius: '4px',
                                            background: 'rgba(245, 158, 11, 0.12)',
                                            color: '#F59E0B',
                                            border: '1px solid rgba(245, 158, 11, 0.3)'
                                        }}>
                                            {activeVersionStatus === 1 || activeVersionStatus === 'Committed' ? 'COMMITTED' : 
                                             activeVersionStatus === 2 || activeVersionStatus === 'Merged' ? 'MERGED' : 'READ-ONLY'}
                                        </span>
                                    )}
                                </h3>
                                <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '0.2rem' }}>
                                    {isProduction 
                                        ? 'The core version. Evaluations read from Production if no version header or active releases exist.' 
                                        : !isVersionActive
                                            ? 'This version has been committed or merged and is read-only. Switch back to Production or create a new version to make edits.'
                                            : 'Draft version. Changes made to variables or rules remain isolated until committed and merged.'}
                                </p>
                            </div>
                            
                            {!isProduction && isVersionActive && (
                                <button
                                    onClick={() => setIsCommitModalOpen(true)}
                                    className="btn-primary"
                                    style={{ background: 'var(--accent)', color: 'white', display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.5rem 1rem' }}
                                >
                                    <i className="fa-solid fa-check"></i> Commit Changes
                                </button>
                            )}
                        </div>

                        {isProduction ? (
                            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '300px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', color: 'var(--text-secondary)', padding: '2rem', textAlign: 'center', gap: '1rem' }}>
                                <i className="fa-solid fa-code-version" style={{ fontSize: '3rem', opacity: 0.3 }}></i>
                                <div>
                                    <h4 style={{ margin: 0, color: 'var(--text-primary)' }}>Production Version Active</h4>
                                    <p style={{ fontSize: '0.85rem', margin: '0.25rem 0 0 0', maxWidth: '400px' }}>
                                        To begin modifying features and rules safely in isolation, create a new working version or switch to a draft version from the sidebar.
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                                {/* Left side: Diff log */}
                                <div style={{ flex: 1.2, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                    <h3 style={{ fontSize: '1rem', fontWeight: 600, margin: 0, color: 'var(--text-primary)' }}>
                                        Pending Changes Log ({versionDiff.length})
                                    </h3>
                                    
                                    {loadingDiff ? (
                                        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem 0' }}><div className="loader"></div></div>
                                    ) : versionDiff.length === 0 ? (
                                        <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontStyle: 'italic', margin: 0 }}>
                                            No modifications made in this version yet. Go to features or rules dashboard to start editing.
                                        </p>
                                    ) : (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxHeight: '500px', overflowY: 'auto' }}>
                                            {versionDiff.map((diff, index) => {
                                                const actionColor = diff.action === 'Added' 
                                                    ? 'var(--success)' 
                                                    : diff.action === 'Deleted' 
                                                        ? 'var(--error)' 
                                                        : '#F59E0B';
                                                
                                                return (
                                                    <div 
                                                        key={index} 
                                                        onClick={() => handlePendingChangeClick(diff)}
                                                        style={{ 
                                                            padding: '0.75rem', 
                                                            border: '1px solid var(--border)', 
                                                            borderRadius: '6px', 
                                                            background: 'var(--bg-secondary)', 
                                                            fontSize: '0.85rem',
                                                            cursor: 'pointer',
                                                            transition: 'border-color 0.2s, box-shadow 0.2s'
                                                        }} 
                                                        className="table-row-hover"
                                                    >
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600, marginBottom: '0.25rem' }}>
                                                            <span style={{ color: 'var(--text-primary)' }}>{diff.name} <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>({diff.type})</span></span>
                                                            <span style={{ color: actionColor }}>{diff.action}</span>
                                                        </div>
                                                        <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', fontFamily: 'monospace' }}>
                                                            {diff.details}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>

                                {/* Right side: Compatibility Check */}
                                <div style={{ flex: 1, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                                    <div>
                                        <h3 style={{ fontSize: '1rem', fontWeight: 600, margin: 0, color: 'var(--text-primary)' }}>Variant Compatibility Validation</h3>
                                        <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '0.2rem' }}>
                                            Run a dry-run check of all saved variants against this version's modified rules to detect if selections break compatibility.
                                        </p>
                                    </div>

                                    <button
                                        onClick={handleRunCompatibilityCheck}
                                        className="btn-secondary"
                                        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', padding: '0.6rem 1rem' }}
                                        disabled={loadingValidation}
                                    >
                                        {loadingValidation ? (
                                            <>Checking...</>
                                        ) : (
                                            <><i className="fa-solid fa-shield-halved"></i> Run Compatibility Verification</>
                                        )}
                                    </button>

                                    {validationResults && (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '0.5rem' }}>
                                            <h4 style={{ fontSize: '0.85rem', margin: 0 }}>Results:</h4>
                                            
                                            {validationResults.length === 0 ? (
                                                <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontStyle: 'italic', margin: 0 }}>No variants defined in store to validate.</p>
                                            ) : (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                                    {validationResults.map((r, i) => (
                                                        <div key={i} style={{ border: '1px solid var(--border)', borderRadius: '6px', padding: '0.75rem', background: 'var(--bg-secondary)' }}>
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
                                                                <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>{r.name}</span>
                                                                <span style={{
                                                                    fontSize: '0.7rem',
                                                                    fontWeight: 700,
                                                                    padding: '0.15rem 0.4rem',
                                                                    borderRadius: '4px',
                                                                    background: r.isCompatible ? 'rgba(5, 150, 105, 0.1)' : 'rgba(220, 38, 38, 0.1)',
                                                                    color: r.isCompatible ? 'var(--success)' : 'var(--error)'
                                                                }}>
                                                                    {r.isCompatible ? 'COMPATIBLE' : 'BROKEN'}
                                                                </span>
                                                            </div>
                                                            {r.errors.length > 0 && (
                                                                <ul style={{ paddingLeft: '1.2rem', margin: '0.25rem 0 0 0', fontSize: '0.78rem', color: 'var(--error)' }}>
                                                                    {r.errors.map((err, j) => <li key={j}>{err}</li>)}
                                                                </ul>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'merge-queue' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }} className="fade-in">
                        <h3 style={{ fontSize: '1.1rem', margin: 0 }}>Pending Commits Review Queue</h3>
                        
                        {commits.filter(c => c.status === 0 || c.status === 'Pending').length === 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '250px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', color: 'var(--text-secondary)', padding: '2rem', textAlign: 'center', gap: '0.75rem' }}>
                                <i className="fa-solid fa-code-pull-request" style={{ fontSize: '2.5rem', opacity: 0.3 }}></i>
                                <div>
                                    <h4 style={{ margin: 0, color: 'var(--text-primary)' }}>Queue is empty</h4>
                                    <p style={{ fontSize: '0.85rem', margin: '0.25rem 0 0 0' }}>No pending draft commits require review at this time.</p>
                                </div>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                {commits.filter(c => c.status === 0 || c.status === 'Pending').map(commit => {
                                    let parsedChanges = [];
                                    try {
                                        parsedChanges = JSON.parse(commit.changesJson);
                                    } catch (e) {}
                                    
                                    return (
                                        <div key={commit.id} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '1px solid var(--border)', paddingBottom: '0.75rem' }}>
                                                <div>
                                                    <h4 style={{ margin: 0, fontSize: '0.95rem', color: 'var(--text-primary)' }}>"{commit.comment}"</h4>
                                                    <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                                                        Submitted by <strong>{commit.committedBy}</strong> on {new Date(commit.committedAt).toLocaleString()}
                                                    </p>
                                                </div>
                                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                    <button
                                                        onClick={() => handleReviewCommitClick(commit)}
                                                        className="btn-secondary"
                                                        style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0.45rem 1rem', fontSize: '0.85rem' }}
                                                    >
                                                        <i className="fa-solid fa-code-compare"></i> Review Changes
                                                    </button>
                                                    <button
                                                        onClick={() => handleMergeCommit(commit.id)}
                                                        className="btn-primary"
                                                        style={{ background: 'var(--success)', color: 'white', display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0.45rem 1rem', fontSize: '0.85rem' }}
                                                    >
                                                        <i className="fa-solid fa-code-merge"></i> Approve & Merge
                                                    </button>
                                                </div>
                                            </div>

                                            <div>
                                                <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '0.5rem' }}>Atomized Changes Included:</span>
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                                    {parsedChanges.map((change, idx) => {
                                                        const actionColor = change.action === 'Added' 
                                                            ? 'rgba(5, 150, 105, 0.15)' 
                                                            : change.action === 'Deleted' 
                                                                ? 'rgba(220, 38, 38, 0.15)' 
                                                                : 'rgba(245, 158, 11, 0.15)';
                                                        const txtColor = change.action === 'Added' 
                                                            ? 'var(--success)' 
                                                            : change.action === 'Deleted' 
                                                                ? 'var(--error)' 
                                                                : '#F59E0B';
                                                        
                                                        return (
                                                            <span key={idx} style={{ padding: '0.25rem 0.5rem', borderRadius: '4px', background: actionColor, color: txtColor, fontSize: '0.75rem', border: `1px solid ${txtColor}50` }}>
                                                                <strong>{change.action}</strong>: {change.name} ({change.type})
                                                            </span>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'releases' && (
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }} className="fade-in">
                        {/* Publish form */}
                        <div style={{ flex: 1, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                            <h3 style={{ fontSize: '1rem', fontWeight: 600, margin: 0, color: 'var(--text-primary)' }}>Publish New Release</h3>
                            <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', margin: 0 }}>
                                Creating a release clones the chosen version state into an immutable Release version. Deployments can use releases for both production configurations and test variations.
                            </p>

                            <form onSubmit={handlePublishRelease} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>Release tag / version name</label>
                                    <input
                                        type="text"
                                        placeholder="e.g. v1.0.0"
                                        value={releaseName}
                                        onChange={e => setReleaseName(e.target.value)}
                                        className="input-field"
                                        required
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>Source Version</label>
                                    <select
                                        value={releaseSourceVersionId}
                                        onChange={e => setReleaseSourceVersionId(e.target.value)}
                                        className="input-field"
                                        style={{ width: '100%', cursor: 'pointer' }}
                                    >
                                        <option value="00000000-0000-0000-0000-000000000000">Production (Production Release)</option>
                                        {versions.filter(b => b.id !== '00000000-0000-0000-0000-000000000000' && b.type !== 2).map(b => (
                                            <option key={b.id} value={b.id}>Draft: {b.name} (Non-Production Test Release)</option>
                                        ))}
                                    </select>
                                </div>
                                <button type="submit" className="btn-primary" style={{ background: 'var(--text-primary)', color: 'var(--bg-primary)' }}>
                                    Publish Release
                                </button>
                            </form>
                        </div>

                        {/* History Log */}
                        <div style={{ flex: 1.5, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            <h3 style={{ fontSize: '1rem', fontWeight: 600, margin: 0, color: 'var(--text-primary)' }}>Releases Log</h3>
                            
                            {versions.filter(b => b.type === 2 || b.type === 'Release').length === 0 ? (
                                <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontStyle: 'italic', margin: 0 }}>
                                    No release tag has been published yet.
                                </p>
                            ) : (
                                <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', textAlign: 'left' }}>
                                        <thead style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border)' }}>
                                            <tr>
                                                <th style={{ padding: '0.75rem 1rem', color: 'var(--text-secondary)' }}>Tag / Version</th>
                                                <th style={{ padding: '0.75rem 1rem', color: 'var(--text-secondary)' }}>Type</th>
                                                <th style={{ padding: '0.75rem 1rem', color: 'var(--text-secondary)' }}>Published By</th>
                                                <th style={{ padding: '0.75rem 1rem', color: 'var(--text-secondary)' }}>Published Date</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {versions.filter(b => b.type === 2 || b.type === 'Release').map(release => (
                                                <tr key={release.id} style={{ borderBottom: '1px solid var(--border)' }}>
                                                    <td style={{ padding: '0.75rem 1rem', fontWeight: 600, color: 'var(--accent)' }}>{release.name}</td>
                                                    <td style={{ padding: '0.75rem 1rem' }}>
                                                        <span style={{
                                                            padding: '0.125rem 0.375rem',
                                                            borderRadius: '4px',
                                                            fontSize: '0.75rem',
                                                            fontWeight: 600,
                                                            background: release.isNonProduction ? 'rgba(239, 68, 68, 0.1)' : 'rgba(16, 185, 129, 0.1)',
                                                            color: release.isNonProduction ? '#ef4444' : '#10b981'
                                                        }}>
                                                            {release.isNonProduction ? 'Test' : 'Production'}
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: '0.75rem 1rem', color: 'var(--text-primary)' }}>{release.createdBy}</td>
                                                    <td style={{ padding: '0.75rem 1rem', color: 'var(--text-secondary)' }}>{new Date(release.createdAt).toLocaleString()}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {activeTab === 'commit-history' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }} className="fade-in">
                        <div>
                            <h3 style={{ fontSize: '1.1rem', margin: 0, color: 'var(--text-primary)' }}>Production Commit History</h3>
                            <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', margin: '0.25rem 0 0 0' }}>
                                A linear history of all commits merged into the Production version. You can review the diffs or revert Production back to any version.
                            </p>
                        </div>

                        {commits.filter(c => c.status === 3 || c.status === 'Merged').length === 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '250px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', color: 'var(--text-secondary)', padding: '2rem', textAlign: 'center', gap: '0.75rem' }}>
                                <i className="fa-solid fa-clock-rotate-left" style={{ fontSize: '2.5rem', opacity: 0.3 }}></i>
                                <div>
                                    <h4 style={{ margin: 0, color: 'var(--text-primary)' }}>No merged commits found</h4>
                                    <p style={{ fontSize: '0.85rem', margin: '0.25rem 0 0 0' }}>Commits will appear here once draft versions are approved and merged.</p>
                                </div>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                {commits
                                    .filter(c => c.status === 3 || c.status === 'Merged')
                                    .sort((a, b) => new Date(b.committedAt) - new Date(a.committedAt))
                                    .map(commit => {
                                        let parsedChanges = [];
                                        try {
                                            parsedChanges = JSON.parse(commit.changesJson);
                                        } catch (e) {}
                                        
                                        const isRevertCommit = commit.comment.startsWith('Reverted Production');

                                        return (
                                            <div key={commit.id} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '1px solid var(--border)', paddingBottom: '0.75rem' }}>
                                                    <div>
                                                        <h4 style={{ margin: 0, fontSize: '0.95rem', color: 'var(--text-primary)' }}>
                                                            {commit.comment}
                                                            <span style={{
                                                                marginLeft: '0.5rem',
                                                                fontSize: '0.7rem',
                                                                fontWeight: 700,
                                                                padding: '0.15rem 0.4rem',
                                                                borderRadius: '4px',
                                                                background: isRevertCommit ? 'rgba(239, 68, 68, 0.1)' : 'rgba(59, 130, 246, 0.1)',
                                                                color: isRevertCommit ? 'var(--error)' : 'var(--accent)',
                                                                border: isRevertCommit ? '1px solid rgba(239, 68, 68, 0.3)' : '1px solid rgba(59, 130, 246, 0.3)'
                                                            }}>
                                                                {isRevertCommit ? 'REVERT' : 'MERGED'}
                                                            </span>
                                                        </h4>
                                                        <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                                                            Committed by <strong>{commit.committedBy}</strong> on {new Date(commit.committedAt).toLocaleString()}
                                                            {commit.reviewedBy && ` • Approved by ${commit.reviewedBy}`}
                                                        </p>
                                                    </div>
                                                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                        <button
                                                            onClick={() => handleReviewCommitClick(commit)}
                                                            className="btn-secondary"
                                                            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0.45rem 1rem', fontSize: '0.85rem' }}
                                                        >
                                                            <i className="fa-solid fa-code-compare"></i> Review Changes
                                                        </button>
                                                        <button
                                                            onClick={() => handleRevertCommit(commit.id)}
                                                            className="btn-primary"
                                                            style={{ 
                                                                background: 'var(--text-primary)', 
                                                                color: 'var(--bg-primary)', 
                                                                display: 'inline-flex', 
                                                                alignItems: 'center', 
                                                                gap: '0.3rem', 
                                                                padding: '0.45rem 1rem', 
                                                                fontSize: '0.85rem',
                                                                opacity: commit.snapshotJson ? 1 : 0.5,
                                                                cursor: commit.snapshotJson ? 'pointer' : 'not-allowed'
                                                            }}
                                                            disabled={!commit.snapshotJson}
                                                            title={!commit.snapshotJson ? "Revert unavailable for legacy commits without snapshots." : "Restore Production to this state"}
                                                        >
                                                            <i className="fa-solid fa-clock-rotate-left"></i> Revert to this version
                                                        </button>
                                                    </div>
                                                </div>

                                                {parsedChanges.length > 0 && (
                                                    <div>
                                                        <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '0.5rem' }}>Atomized Changes:</span>
                                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                                            {parsedChanges.map((change, idx) => {
                                                                const actionColor = change.action === 'Added' 
                                                                    ? 'rgba(5, 150, 105, 0.15)' 
                                                                    : change.action === 'Deleted' 
                                                                        ? 'rgba(220, 38, 38, 0.15)' 
                                                                        : 'rgba(245, 158, 11, 0.15)';
                                                                const txtColor = change.action === 'Added' 
                                                                    ? 'var(--success)' 
                                                                    : change.action === 'Deleted' 
                                                                        ? 'var(--error)' 
                                                                        : '#F59E0B';
                                                                
                                                                return (
                                                                    <span key={idx} style={{ padding: '0.25rem 0.5rem', borderRadius: '4px', background: actionColor, color: txtColor, fontSize: '0.75rem', border: `1px solid ${txtColor}50` }}>
                                                                        <strong>{change.action}</strong>: {change.name} ({change.type}){change.familyName && <span style={{ opacity: 0.8, fontStyle: 'italic', marginLeft: '0.25rem' }}>• {change.familyName}</span>}
                                                                    </span>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* --- Modals --- */}

            {/* 1. Create working version modal */}
            {isCreateModalOpen && (
                <ModalPortal>
                <div className="modal-overlay fade-in" onClick={() => setIsCreateModalOpen(false)}>
                    <div className="modal-content" style={{ maxWidth: '450px', width: '90%' }} onClick={e => e.stopPropagation()}>
                        <h2 style={{ color: 'var(--text-primary)', margin: 0, fontSize: '1.25rem' }}>Create Working Draft Version</h2>
                        <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', margin: '0.5rem 0 1.5rem 0' }}>
                            Creates an isolated sandbox environment. All feature structures and rules will be cloned from the selected parent.
                        </p>

                        <form onSubmit={handleCreateVersion} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                            <div>
                                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.35rem' }}>Version Name</label>
                                <input
                                    type="text"
                                    placeholder="e.g. dev-selections-fix"
                                    value={newVersionName}
                                    onChange={e => setNewVersionName(e.target.value)}
                                    className="input-field"
                                    required
                                />
                            </div>

                            <div>
                                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.35rem' }}>Parent Version</label>
                                <select
                                    value={newVersionParent}
                                    onChange={e => setNewVersionParent(e.target.value)}
                                    className="input-field"
                                >
                                    <option value="00000000-0000-0000-0000-000000000000">Production</option>
                                    {versions.filter(b => b.type === 1 || b.type === 'Draft').map(b => (
                                        <option key={b.id} value={b.id}>{b.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="modal-actions">
                                <button type="button" className="btn-secondary" onClick={() => setIsCreateModalOpen(false)}>Cancel</button>
                                <button type="submit" className="btn-primary" style={{ background: 'var(--text-primary)', color: 'var(--bg-primary)' }}>Create Version</button>
                            </div>
                        </form>
                    </div>
                </div>
                </ModalPortal>
            )}

            {/* 2. Submit commit modal */}
            {isCommitModalOpen && (
                <ModalPortal>
                <div className="modal-overlay fade-in" onClick={() => setIsCommitModalOpen(false)}>
                    <div className="modal-content" style={{ maxWidth: '450px', width: '90%' }} onClick={e => e.stopPropagation()}>
                        <h2 style={{ color: 'var(--text-primary)', margin: 0, fontSize: '1.25rem' }}>Commit Version Changes</h2>
                        <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', margin: '0.5rem 0 1.25rem 0' }}>
                            Write a summary comment of the modifications. Submitting freezes this version and puts it in the Merge Queue for review.
                        </p>

                        <form onSubmit={handleSubmitCommit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                            <div>
                                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.35rem' }}>Commit Message</label>
                                <textarea
                                    placeholder="Describe your changes..."
                                    value={commitComment}
                                    onChange={e => setCommitComment(e.target.value)}
                                    className="textarea-field"
                                    style={{ minHeight: '80px' }}
                                    required
                                />
                            </div>

                            <div className="modal-actions">
                                <button type="button" className="btn-secondary" onClick={() => setIsCommitModalOpen(false)}>Cancel</button>
                                <button type="submit" className="btn-primary" style={{ background: 'var(--accent)', color: 'white' }}>Submit Commit</button>
                            </div>
                        </form>
                    </div>
                </div>
                </ModalPortal>
            )}

            <DiffModal
                isOpen={isDiffModalOpen}
                onClose={() => setIsDiffModalOpen(false)}
                diffs={selectedDiffs}
                title={modalTitle}
                commitComment={modalCommitComment}
                onMerge={modalOnMerge}
                isMerging={isMergingState}
                onGoToEditor={modalOnGoToEditor}
            />
        </div>
    );
}

function DiffView({ diff }) {
    if (!diff) return null;

    const parseRow = (rowStr) => {
        const cells = {};
        if (!rowStr) return cells;
        const parts = rowStr.split(', ');
        parts.forEach(part => {
            const colonIdx = part.indexOf(': ');
            if (colonIdx !== -1) {
                const varName = part.substring(0, colonIdx);
                const value = part.substring(colonIdx + 2);
                cells[varName] = value;
            }
        });
        return cells;
    };

    const lines = [];
    let addedRows = [];
    let deletedRows = [];
    let hasTableDiff = false;
    let columns = [];

    if (diff.type === 'RuleSet' && diff.details) {
        if (diff.action === 'Added') {
            lines.push({ type: 'add', text: `+ Entity: ${diff.name} (${diff.type})` });
            const addedRowsIdx = diff.details.indexOf('Added rows: [');
            if (addedRowsIdx !== -1) {
                const headerText = diff.details.substring(0, addedRowsIdx).trim();
                lines.push({ type: 'add', text: `+ Details: ${headerText}` });
                const rowsStr = diff.details.substring(addedRowsIdx + 'Added rows: ['.length, diff.details.length - 1);
                if (rowsStr) {
                    addedRows = rowsStr.split('; ').map(parseRow);
                }
            } else {
                lines.push({ type: 'add', text: `+ Details: ${diff.details}` });
            }
        } else if (diff.action === 'Deleted') {
            lines.push({ type: 'delete', text: `- Entity: ${diff.name} (${diff.type})` });
            const deletedRowsIdx = diff.details.indexOf('Deleted rows: [');
            if (deletedRowsIdx !== -1) {
                const headerText = diff.details.substring(0, deletedRowsIdx).trim();
                lines.push({ type: 'delete', text: `- Details: ${headerText}` });
                const rowsStr = diff.details.substring(deletedRowsIdx + 'Deleted rows: ['.length, diff.details.length - 1);
                if (rowsStr) {
                    deletedRows = rowsStr.split('; ').map(parseRow);
                }
            } else {
                lines.push({ type: 'delete', text: `- Details: ${diff.details}` });
            }
        } else if (diff.action === 'Modified') {
            lines.push({ type: 'neutral', text: `Entity: ${diff.name} (${diff.type})` });
            const detailParts = diff.details.split('; ');
            detailParts.forEach(part => {
                if (part.startsWith('Renamed from')) {
                    const match = part.match(/Renamed from '(.*)'/);
                    const oldName = match ? match[1] : '';
                    lines.push({ type: 'delete', text: `- Name: ${oldName}` });
                    lines.push({ type: 'add', text: `+ Name: ${diff.name}` });
                } else if (part.startsWith('Added rows:')) {
                    const rowsStr = part.substring('Added rows: ['.length, part.length - 1);
                    if (rowsStr) {
                        addedRows = rowsStr.split('; ').map(parseRow);
                    }
                } else if (part.startsWith('Deleted rows:')) {
                    const rowsStr = part.substring('Deleted rows: ['.length, part.length - 1);
                    if (rowsStr) {
                        deletedRows = rowsStr.split('; ').map(parseRow);
                    }
                } else if (part.startsWith('Row count changed from')) {
                    const match = part.match(/Row count changed from (\d+) to (\d+)/);
                    if (match) {
                        lines.push({ type: 'delete', text: `- Rows: ${match[1]}` });
                        lines.push({ type: 'add', text: `+ Rows: ${match[2]}` });
                    } else {
                        lines.push({ type: 'neutral', text: part });
                    }
                } else {
                    lines.push({ type: 'neutral', text: part });
                }
            });
        }

        if (addedRows.length > 0 || deletedRows.length > 0) {
            hasTableDiff = true;
            const columnsSet = new Set();
            addedRows.forEach(row => Object.keys(row).forEach(col => columnsSet.add(col)));
            deletedRows.forEach(row => Object.keys(row).forEach(col => columnsSet.add(col)));
            columns = Array.from(columnsSet).sort();
        }
    } else {
        if (diff.action === 'Added') {
            lines.push({ type: 'add', text: `+ Entity: ${diff.name} (${diff.type})` });
            if (diff.details) {
                lines.push({ type: 'add', text: `+ Details: ${diff.details}` });
            }
        } else if (diff.action === 'Deleted') {
            lines.push({ type: 'delete', text: `- Entity: ${diff.name} (${diff.type})` });
            if (diff.details) {
                lines.push({ type: 'delete', text: `- Details: ${diff.details}` });
            }
        } else if (diff.action === 'Modified') {
            lines.push({ type: 'neutral', text: `Entity: ${diff.name} (${diff.type})` });
            if (diff.details) {
                const detailParts = diff.details.split('; ');
                detailParts.forEach(part => {
                    if (part.startsWith('Renamed from')) {
                        const match = part.match(/Renamed from '(.*)'/);
                        const oldName = match ? match[1] : '';
                        lines.push({ type: 'delete', text: `- Name: ${oldName}` });
                        lines.push({ type: 'add', text: `+ Name: ${diff.name}` });
                    } else if (part.startsWith('Global status changed to')) {
                        const match = part.match(/Global status changed to (.*)/);
                        const newStatus = match ? match[1] : '';
                        const oldStatus = newStatus === 'true' ? 'false' : 'true';
                        lines.push({ type: 'delete', text: `- Global: ${oldStatus}` });
                        lines.push({ type: 'add', text: `+ Global: ${newStatus}` });
                    } else if (part.startsWith('Added values:')) {
                        const vals = part.replace('Added values: [', '').replace(']', '');
                        vals.split(', ').forEach(v => {
                            lines.push({ type: 'add', text: `+ Value: ${v}` });
                        });
                    } else if (part.startsWith('Removed values:')) {
                        const vals = part.replace('Removed values: [', '').replace(']', '');
                        vals.split(', ').forEach(v => {
                            lines.push({ type: 'delete', text: `- Value: ${v}` });
                        });
                    } else {
                        lines.push({ type: 'neutral', text: part });
                    }
                });
            }
        }
    }

    return (
        <div style={{ marginBottom: '1rem' }}>
            <div style={{ 
                fontFamily: 'monospace', 
                fontSize: '0.85rem', 
                background: 'var(--bg-primary)', 
                padding: '1rem', 
                borderRadius: '6px', 
                border: '1px solid var(--border)', 
                display: 'flex', 
                flexDirection: 'column', 
                gap: '0.25rem', 
                overflowX: 'auto'
            }}>
                {lines.map((line, idx) => {
                    let color = 'var(--text-primary)';
                    let bg = 'transparent';
                    if (line.type === 'add') {
                        color = 'var(--success)';
                        bg = 'rgba(5, 150, 105, 0.08)';
                    } else if (line.type === 'delete') {
                        color = 'var(--error)';
                        bg = 'rgba(220, 38, 38, 0.08)';
                    }
                    return (
                        <div key={idx} style={{ 
                            color, 
                            background: bg, 
                            padding: '0.2rem 0.5rem', 
                            borderRadius: '4px', 
                            whiteSpace: 'pre-wrap',
                            textDecoration: line.type === 'delete' ? 'line-through' : 'none'
                        }}>
                            {line.text}
                        </div>
                    );
                })}
            </div>
            
            {hasTableDiff && (
                <div style={{ marginTop: '0.75rem', overflowX: 'auto' }}>
                    <table style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        fontSize: '0.85rem',
                        textAlign: 'left',
                        fontFamily: 'inherit',
                        border: '1px solid var(--border)',
                        borderRadius: '6px',
                        overflow: 'hidden'
                    }}>
                        <thead>
                            <tr style={{ background: 'var(--bg-secondary)', borderBottom: '2px solid var(--border)' }}>
                                <th style={{ padding: '0.6rem 0.75rem', width: '90px', fontWeight: 'bold', color: 'var(--text-primary)' }}>Action</th>
                                {columns.map(col => (
                                    <th key={col} style={{ padding: '0.6rem 0.75rem', fontWeight: 'bold', color: 'var(--text-primary)', borderLeft: '1px solid var(--border)' }}>
                                        {col}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {deletedRows.map((row, rIdx) => (
                                <tr key={`del-${rIdx}`} style={{ 
                                    background: 'rgba(220, 38, 38, 0.04)', 
                                    color: 'var(--error)',
                                    borderBottom: '1px solid var(--border)',
                                    textDecoration: 'line-through'
                                }}>
                                    <td style={{ padding: '0.6rem 0.75rem', fontWeight: 'bold' }}>
                                        <i className="fa-solid fa-minus" style={{ marginRight: '0.35rem' }}></i>Delete
                                    </td>
                                    {columns.map(col => (
                                        <td key={col} style={{ padding: '0.6rem 0.75rem', borderLeft: '1px solid var(--border)' }}>
                                            {row[col] || ''}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                            {addedRows.map((row, rIdx) => (
                                <tr key={`add-${rIdx}`} style={{ 
                                    background: 'rgba(5, 150, 105, 0.04)', 
                                    color: 'var(--success)',
                                    borderBottom: '1px solid var(--border)'
                                }}>
                                    <td style={{ padding: '0.6rem 0.75rem', fontWeight: 'bold' }}>
                                        <i className="fa-solid fa-plus" style={{ marginRight: '0.35rem' }}></i>Add
                                    </td>
                                    {columns.map(col => (
                                        <td key={col} style={{ padding: '0.6rem 0.75rem', borderLeft: '1px solid var(--border)' }}>
                                            {row[col] || ''}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function DiffModal({ isOpen, onClose, diffs, title, commitComment, onMerge, isMerging, onGoToEditor }) {
    if (!isOpen) return null;

    return (
        <ModalPortal>
        <div className="modal-overlay fade-in" onClick={onClose}>
            <div className="modal-content" style={{ maxWidth: '600px', width: '95%', maxHeight: '80vh', display: 'flex', flexDirection: 'column', padding: '1.5rem' }} onClick={e => e.stopPropagation()}>
                <header style={{ borderBottom: '1px solid var(--border)', paddingBottom: '1rem', marginBottom: '1rem', flexShrink: 0 }}>
                    <h2 style={{ color: 'var(--text-primary)', margin: 0, fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <i className="fa-solid fa-code-compare" style={{ color: 'var(--accent)' }}></i>
                        {title}
                    </h2>
                    {commitComment && (
                        <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.85rem', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                            "{commitComment}"
                        </p>
                    )}
                </header>

                <div style={{ flex: 1, overflowY: 'auto', paddingRight: '0.5rem' }}>
                    {diffs.map((diff, index) => (
                        <div key={index}>
                            <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', color: 'var(--text-primary)', display: 'flex', justifyContent: 'space-between' }}>
                                <span>{diff.name} <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>({diff.type})</span>{diff.familyName && <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginLeft: '0.5rem', fontStyle: 'italic' }}>({diff.familyName})</span>}</span>
                                <span style={{ color: diff.action === 'Added' ? 'var(--success)' : diff.action === 'Deleted' ? 'var(--error)' : '#F59E0B' }}>
                                    {diff.action}
                                </span>
                            </h4>
                            <DiffView diff={diff} />
                        </div>
                    ))}
                </div>

                <div className="modal-actions" style={{ borderTop: '1px solid var(--border)', paddingTop: '1rem', marginTop: '1rem', flexShrink: 0 }}>
                    <button type="button" className="btn-secondary" onClick={onClose}>Close</button>
                    {onGoToEditor && (
                        <button 
                            type="button" 
                            className="btn-primary" 
                            style={{ background: 'var(--accent)', color: 'white' }}
                            onClick={onGoToEditor}
                        >
                            <i className="fa-solid fa-arrow-right-to-bracket"></i> Go to Editor
                        </button>
                    )}
                    {onMerge && (
                        <button 
                            type="button" 
                            className="btn-primary" 
                            style={{ background: 'var(--success)', color: 'white' }}
                            onClick={onMerge}
                            disabled={isMerging}
                        >
                            {isMerging ? 'Merging...' : <><i className="fa-solid fa-code-merge"></i> Approve & Merge</>}
                        </button>
                    )}
                </div>
            </div>
        </div>
        </ModalPortal>
    );
}
